import React, { useState, useRef, useEffect } from 'react';
import Icon from '../AppIcon';
import Image from '../AppImage';
import Button from './Button';

const TrailPhotoGallery = ({ 
  images = [], 
  title = "Trail Gallery",
  className = "" 
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const sliderRef = useRef(null);
  const thumbnailsRef = useRef(null);

  // Default images if none provided
  const defaultImages = [
    {
      id: 1,
      src: '/assets/images/trails/trail-1.jpg',
      alt: 'Mountain trail with scenic overlook',
      caption: 'Scenic mountain overlook along the heritage trail'
    },
    {
      id: 2,
      src: '/assets/images/trails/trail-2.jpg',
      alt: 'Historic monastery on the trail',
      caption: 'Ancient monastery nestled in the valley'
    },
    {
      id: 3,
      src: '/assets/images/trails/trail-3.jpg',
      alt: 'Forest path through heritage site',
      caption: 'Peaceful forest path connecting heritage sites'
    },
    {
      id: 4,
      src: '/assets/images/trails/trail-4.jpg',
      alt: 'Traditional village along the route',
      caption: 'Traditional village showcasing local culture'
    },
    {
      id: 5,
      src: '/assets/images/trails/trail-5.jpg',
      alt: 'River crossing on heritage trail',
      caption: 'Historic bridge crossing mountain river'
    }
  ];

  const galleryImages = images?.length > 0 ? images : defaultImages;

  useEffect(() => {
    // Simulate loading delay for better UX
    const timer = setTimeout(() => setIsLoading(false), 500);
    return () => clearTimeout(timer);
  }, []);

  const goToSlide = (index) => {
    setCurrentIndex(index);
    if (sliderRef?.current) {
      const slideWidth = sliderRef?.current?.offsetWidth;
      sliderRef?.current?.scrollTo({
        left: slideWidth * index,
        behavior: 'smooth'
      });
    }
  };

  const goToPrevious = () => {
    const newIndex = currentIndex === 0 ? galleryImages?.length - 1 : currentIndex - 1;
    goToSlide(newIndex);
  };

  const goToNext = () => {
    const newIndex = currentIndex === galleryImages?.length - 1 ? 0 : currentIndex + 1;
    goToSlide(newIndex);
  };

  const handleTouchStart = useRef(null);
  const handleTouchEnd = (e) => {
    if (!handleTouchStart?.current) return;
    
    const touchEnd = e?.changedTouches?.[0]?.clientX;
    const touchStart = handleTouchStart?.current;
    const diff = touchStart - touchEnd;
    
    if (Math.abs(diff) > 50) { // Minimum swipe distance
      if (diff > 0) {
        goToNext();
      } else {
        goToPrevious();
      }
    }
    
    handleTouchStart.current = null;
  };

  if (isLoading) {
    return (
      <div className={`bg-card rounded-lg shadow-card overflow-hidden ${className}`}>
        <div className="aspect-[16/9] bg-muted animate-pulse" />
        <div className="p-4">
          <div className="h-4 bg-muted rounded animate-pulse mb-2" />
          <div className="flex gap-2">
            {[...Array(5)]?.map((_, i) => (
              <div key={i} className="w-16 h-12 bg-muted rounded animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-card rounded-lg shadow-card overflow-hidden ${className}`}>
      {/* Main Image Display */}
      <div className="relative aspect-[16/9] bg-muted overflow-hidden group">
        <div
          ref={sliderRef}
          className="flex h-full momentum-scroll"
          style={{ scrollSnapType: 'x mandatory' }}
          onTouchStart={(e) => handleTouchStart.current = e?.touches?.[0]?.clientX}
          onTouchEnd={handleTouchEnd}
        >
          {galleryImages?.map((image, index) => (
            <div
              key={image?.id}
              className="min-w-full h-full relative"
              style={{ scrollSnapAlign: 'start' }}
            >
              <Image
                src={image?.src}
                alt={image?.alt}
                className="w-full h-full object-cover"
                loading={index === 0 ? 'eager' : 'lazy'}
              />
              {image?.caption && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                  <p className="text-white text-sm font-medium">
                    {image?.caption}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Navigation Arrows */}
        <Button
          variant="ghost"
          size="icon"
          onClick={goToPrevious}
          className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/20 hover:bg-black/40 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-normal"
          aria-label="Previous image"
        >
          <Icon name="ChevronLeft" size={24} />
        </Button>
        
        <Button
          variant="ghost"
          size="icon"
          onClick={goToNext}
          className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/20 hover:bg-black/40 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-normal"
          aria-label="Next image"
        >
          <Icon name="ChevronRight" size={24} />
        </Button>

        {/* Image Counter */}
        <div className="absolute top-4 right-4 bg-black/60 text-white px-3 py-1 rounded-full text-sm font-medium">
          {currentIndex + 1} / {galleryImages?.length}
        </div>
      </div>
      {/* Thumbnail Navigation */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-semibold text-base">{title}</h3>
          <div className="flex gap-1">
            {galleryImages?.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-2 h-2 rounded-full transition-all duration-fast ${
                  index === currentIndex 
                    ? 'bg-primary w-6' :'bg-muted hover:bg-muted-foreground/30'
                }`}
                aria-label={`Go to image ${index + 1}`}
              />
            ))}
          </div>
        </div>
        
        <div 
          ref={thumbnailsRef}
          className="flex gap-2 overflow-x-auto momentum-scroll pb-2"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {galleryImages?.map((image, index) => (
            <button
              key={image?.id}
              onClick={() => goToSlide(index)}
              className={`flex-shrink-0 w-16 h-12 rounded-md overflow-hidden border-2 transition-all duration-fast ${
                index === currentIndex 
                  ? 'border-primary shadow-sm' 
                  : 'border-transparent hover:border-muted-foreground/30'
              }`}
            >
              <Image
                src={image?.src}
                alt={`Thumbnail ${index + 1}`}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TrailPhotoGallery;